'use client'

import { cn, getLogoProxyImgProps } from '@morpho-org/ui-v6/utils'
import { isLogoProxySrc, toLogoSrc } from '@repo/utils'
import { type Address, blo } from 'blo'
import { cva } from 'class-variance-authority'
import Image from 'next/image'
import { type ComponentProps, forwardRef, type ReactNode, useState } from 'react'

export type AvatarSize = 'xsmall' | 'small' | 'medium' | 'regular' | 'big' | 'large' | number

const avatarVariants = cva(
  // Base styles
  'relative inline-flex aspect-square shrink-0 items-center justify-center',
  {
    variants: {
      size: {
        xsmall: 'size-[14px]',
        small: 'size-5',
        medium: 'size-[24px]',
        regular: 'size-[38px]',
        big: 'size-[60px]',
        large: 'size-20'
      },
      padding: {
        none: 'p-0',
        xs: 'p-[2px]',
        lg: 'p-[7px]'
      }
    },
    compoundVariants: [
      { size: 'xsmall', className: 'p-[0px]' },
      { size: 'small', className: 'p-[2px]' },
      { size: 'medium', className: 'p-[2px]' },
      { size: 'regular', className: 'p-[2px]' },
      { size: 'big', className: 'p-[2px]' },
      { size: 'large', className: 'p-[7px]' }
    ],
    defaultVariants: {
      size: 'small'
    }
  }
)

export function getAvatarSize(size: AvatarSize): number {
  switch (size) {
    case 'xsmall':
      return 14
    case 'small':
      return 20
    case 'medium':
      return 24
    case 'regular':
      return 38
    case 'big':
      return 60
    case 'large':
      return 80
    default:
      return size
  }
}

// Inner padding per named size; keep in sync with avatarVariants' compoundVariants.
export const AVATAR_PADDING: Record<Exclude<AvatarSize, number>, number> = {
  xsmall: 0,
  small: 2,
  medium: 2,
  regular: 2,
  big: 2,
  large: 7
}

export interface BaseAvatarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The size of the avatar. CDN logos are requested at this size, so resize with `size`, not CSS, or they blur. */
  size?: AvatarSize
  /** Padding override */
  padding?: number
  /** Whether the image should be rounded */
  rounded?: boolean
  /** Props to pass to the underlying Image component (not used for cdn.morpho.org SVG logos) */
  imageProps?: Partial<ComponentProps<typeof Image>>
}

export interface WalletAvatarProps extends BaseAvatarProps {
  /** Wallet address to generate blockie from */
  address?: Address | null | undefined
  /** ENS avatar URL */
  ensUrl?: string | null | undefined
  src?: never
  icon?: never
}

export interface SrcAvatarProps extends BaseAvatarProps {
  /** The image source URL */
  src: string | null | undefined
  address?: never
  ensUrl?: never
  icon?: never
}

export interface IconAvatarProps extends BaseAvatarProps {
  /** Element rendered in place of an image, sized to fill the avatar */
  icon: ReactNode
  src?: never
  address?: never
  ensUrl?: never
}

export type AvatarProps = WalletAvatarProps | SrcAvatarProps | IconAvatarProps

/**
 * Server-only Avatar component for ui-v6.
 * Renders an image avatar without any client-side state.
 *
 * @example
 * ```tsx
 * // Basic usage with src
 * <Avatar src="/avatar.png" size="medium" />
 *
 * // With wallet address (generates blockie)
 * <Avatar address="0x..." size="regular" />
 *
 * // With ENS avatar
 * <Avatar ensUrl="https://..." size="regular" />
 *
 * // Fallback (no src/address/ensUrl)
 * <Avatar size="regular" />
 * ```
 */
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      size = 'small',
      padding,
      rounded = false,
      imageProps,
      icon,
      address,
      ensUrl,
      src,
      ...props
    },
    ref
  ) => {
    const [errorSrc, setErrorSrc] = useState<string | null>(null)

    // Compute image source from props; cdn.morpho.org SVGs are routed through
    // the logo proxy (app-relative path) so next/image can optimize them. A
    // proxy failure shows the fallback: retrying the raw SVG would re-download
    // the multi-MB wrappers the proxy exists to keep off the wire.
    const rawImageSrc = (() => {
      if (ensUrl) return ensUrl
      if (address) return blo(address)
      if (src) return src
      return undefined
    })()
    const imageSrc = rawImageSrc === undefined ? undefined : toLogoSrc(rawImageSrc)

    const hasError = imageSrc != null && imageSrc === errorSrc

    // Auto-round for address/ensUrl avatars
    const shouldRound = ensUrl || address ? true : rounded

    const isNumericSize = typeof size === 'number'
    const dimension = getAvatarSize(size)
    // next/image only emits 1x/2x candidates; requesting 2× keeps 3× (iPhone)
    // screens crisp. CSS size-full sets the rendered size.
    const imageDimension = dimension * 2
    const logoPx = dimension - 2 * (isNumericSize ? (padding ?? 2) : AVATAR_PADDING[size])

    // For numeric sizes, use inline styles since Tailwind can't handle dynamic values
    const numericStyles: React.CSSProperties | undefined = isNumericSize
      ? {
          width: dimension,
          height: dimension,
          padding: padding ?? 2,
          position: 'relative',
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          aspectRatio: '1 / 1'
        }
      : undefined

    // Only app-relative paths and cdn.morpho.org (the sole images.remotePatterns
    // host) go through the optimizer; every other src bypasses it.
    const isOptimizable =
      imageSrc != null &&
      ((imageSrc.startsWith('/') && !imageSrc.startsWith('//')) ||
        imageSrc.startsWith('https://cdn.morpho.org/'))

    return (
      <div
        ref={ref}
        className={cn(!isNumericSize && avatarVariants({ size }), className)}
        style={numericStyles}
        {...props}
      >
        {icon ? (
          <div className="relative size-full">{icon}</div>
        ) : imageSrc && !hasError ? (
          <div className="size-full">
            {isLogoProxySrc(imageSrc) ? (
              // next/image can't emit the exact-size 3x candidate these logos need.
              // eslint-disable-next-line @next/next/no-img-element
              <img
                {...getLogoProxyImgProps(imageSrc, logoPx, 'avatar')}
                className={cn('size-full object-contain', shouldRound && 'rounded-full')}
                onError={() => setErrorSrc(imageSrc)}
                // React misses an error that fires before hydration; catch it on mount.
                ref={img => {
                  if (img?.complete && img.naturalWidth === 0) setErrorSrc(imageSrc)
                }}
              />
            ) : (
              <Image
                src={imageSrc}
                width={imageDimension}
                height={imageDimension}
                alt="avatar"
                className={cn('size-full object-contain', shouldRound && 'rounded-full')}
                onError={() => setErrorSrc(imageSrc ?? null)}
                unoptimized={!isOptimizable}
                {...imageProps}
              />
            )}
          </div>
        ) : (
          <AvatarFallback />
        )}
      </div>
    )
  }
)
Avatar.displayName = 'Avatar'

/**
 * Fallback component displayed when Avatar has no src.
 */
const AvatarFallback = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('size-full rounded-full bg-earth', className)} {...props} />
  )
)
AvatarFallback.displayName = 'AvatarFallback'
